import type { SegmentTrackExperimentViewedEvent } from "@beautypie/segment/Track/ExperimentViewed"
import type { SegmentTrackPageElementClickedEvent } from "@beautypie/segment/Track/PageElementClicked"
import type { SegmentTrackPageElementViewedEvent } from "@beautypie/segment/Track/PageElementViewed"
import type { SegmentTrackProductAddedEvent } from "@beautypie/segment/Track/ProductAdded"
import type { SegmentTrackProductClickedEvent } from "@beautypie/segment/Track/ProductClicked"
import type { SegmentTrackProductListViewedEvent } from "@beautypie/segment/Track/ProductListViewed"
import type { SegmentTrackProductRemovedEvent } from "@beautypie/segment/Track/ProductRemoved"
import type { TrackingClicked, TrackingViewed } from "types"

type SegmentBffTracking =
  | SegmentTrackProductAddedEvent
  | SegmentTrackProductRemovedEvent
  | SegmentTrackPageElementViewedEvent
  | SegmentTrackPageElementClickedEvent
  | SegmentTrackProductListViewedEvent
  | SegmentTrackPageElementViewedEvent
  | SegmentTrackProductClickedEvent
  | SegmentTrackExperimentViewedEvent

type EventOf<T> = T extends { event: infer N } ? N : never
type EventUnion = EventOf<SegmentBffTracking>

// eslint-disable-next-line @typescript-eslint/ban-types
type WithoutNullsOrTypeName<T extends object> = Omit<
  {
    readonly [K in keyof T]: T[K] extends Record<string, unknown>
      ? WithoutNullsOrTypeName<T[K]>
      : T[K] extends Array<Record<string, unknown>>
      ? Array<WithoutNullsOrTypeName<T[K][number]>>
      : T[K] extends Array<unknown>
      ? Array<Exclude<T[K][number], null>>
      : Exclude<T[K], null>
  },
  "__typename"
>

const acceptedEvents: EventUnion[] = [
  "Product Added",
  "Product Removed",
  "Page Element Viewed",
  "Page Element Clicked",
  "Product List Viewed",
  "Product Clicked",
  "Experiment Viewed",
]

export const isSegmentTrackEvent = (
  data: unknown
): data is SegmentBffTracking => {
  if (
    typeof data === "object" &&
    data !== null &&
    "event" in data &&
    acceptedEvents.includes((data as { event: EventUnion }).event)
  ) {
    return true
  }
  return false
}
// eslint-disable-next-line @typescript-eslint/ban-types
export const removeNullsAndTypeNames = <T extends object>(
  obj: T
): WithoutNullsOrTypeName<T> => {
  return Object.fromEntries(
    Object.entries(obj)
      .map(([key, value]) => {
        if (Array.isArray(value))
          return [key, value.map(removeNullsAndTypeNames)]
        if (value && typeof value === "object")
          return [key, removeNullsAndTypeNames(value)]
        return [key, value]
      })
      .filter(([key, value]) => value !== null && key !== "__typename")
  )
}

export const mapGraphqlToSegmentTrackEvent = (
  data: TrackingViewed | TrackingClicked
): SegmentBffTracking | undefined => {
  const clearedObject = removeNullsAndTypeNames(data)

  if (isSegmentTrackEvent(clearedObject)) {
    return clearedObject
  }
}
